import React, { useState, useEffect } from 'react';
import { Search, Eye, Mail, X, Check, User, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../api/axios';

const VisualizadoresSelector = ({ onVisualizadoresChange, visualizadores = [], disabled = false, onSearchFocus, onSearchBlur }) => {
    const [usuarios, setUsuarios] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [newVisualizador, setNewVisualizador] = useState({
        email: '',
        nombre_completo: ''
    });
    const [showAddForm, setShowAddForm] = useState(false);
    const [loading, setLoading] = useState(false);

    // Cargar usuarios de la base de datos
    useEffect(() => {
        const fetchUsuarios = async () => {
            try {
                const response = await axios.get('/api/users/');
                setUsuarios(response.data);
            } catch (error) {
                console.error('Error al cargar usuarios:', error);
                toast.error('Error al cargar la lista de usuarios');
            }
        };
        fetchUsuarios();
    }, []);

    // Filtrar usuarios por término de búsqueda
    const filteredUsuarios = usuarios.filter(usuario =>
        usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Agregar visualizador existente
    const handleAddExistingVisualizador = (usuario) => {
        const nuevoVisualizador = {
            user_id: usuario.id,
            email: usuario.email,
            nombre_completo: `${usuario.nombre} ${usuario.apellido}`
        };

        onVisualizadoresChange([...visualizadores, nuevoVisualizador]);
        setSearchTerm('');
        toast.success(`${usuario.nombre} ${usuario.apellido} agregado como visualizador`);
    };

    // Agregar nuevo visualizador por email
    const handleAddNewVisualizador = async () => {
        if (!newVisualizador.email || !newVisualizador.nombre_completo) {
            toast.error('Por favor completa todos los campos');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newVisualizador.email)) {
            toast.error('Por favor ingresa un email válido');
            return;
        }

        const nuevoVisualizador = {
            user_id: null, // No está registrado aún
            email: newVisualizador.email,
            nombre_completo: newVisualizador.nombre_completo
        };

        onVisualizadoresChange([...visualizadores, nuevoVisualizador]);
        setNewVisualizador({ email: '', nombre_completo: '' });
        setShowAddForm(false);
        toast.success(`${newVisualizador.nombre_completo} agregado como visualizador`);
    };

    // Remover visualizador
    const handleRemoveVisualizador = (index) => {
        const nuevosVisualizadores = visualizadores.filter((_, i) => i !== index);
        onVisualizadoresChange(nuevosVisualizadores);
        toast.success('Visualizador removido');
    };

    return (
        <div id="tour-visualizadores" className="space-y-5">
            {/* Título */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900">
                    Visualizadores ({visualizadores.length})
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    Personas que podrán ver el documento cuando esté completado (sin firmar)
                </p>
            </div>

            {/* Búsqueda de usuarios existentes */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar usuarios existentes
                </label>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value)
                            if (e.target.value.trim() && onSearchFocus) {
                                onSearchFocus()
                            } else if (!e.target.value.trim() && onSearchBlur) {
                                onSearchBlur()
                            }
                        }}
                        onFocus={() => {
                            if (onSearchFocus) onSearchFocus()
                        }}
                        onBlur={(e) => {
                            if (!e.target.value.trim() && onSearchBlur) {
                                setTimeout(() => {
                                    if (document.activeElement !== e.target && !e.target.value.trim()) {
                                        onSearchBlur()
                                    }
                                }, 300)
                            }
                        }}
                        disabled={disabled}
                        className={`w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${
                            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="Escribe para buscar..."
                    />
                </div>

                {/* Lista de usuarios filtrados */}
                {searchTerm && !disabled && (
                    <div className="mt-3 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                        {filteredUsuarios.length > 0 ? (
                            <div className="divide-y divide-gray-100">
                                {filteredUsuarios.map((usuario) => (
                                    <div
                                        key={usuario.id}
                                        className="flex items-center justify-between p-4 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 cursor-pointer transition-all duration-200 group"
                                        onClick={() => handleAddExistingVisualizador(usuario)}
                                    >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md flex-shrink-0 group-hover:scale-110 transition-transform">
                                                <Eye className="h-4 w-4 text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-gray-900 text-base truncate">
                                                    {usuario.nombre} {usuario.apellido}
                                                </p>
                                                <p className="text-sm text-gray-600 truncate mt-0.5">{usuario.email}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddExistingVisualizador(usuario);
                                            }}
                                            className="ml-3 flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md group-hover:scale-110"
                                            title="Agregar visualizador"
                                        >
                                            <Plus className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-8 text-center text-gray-500">
                                <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-sm font-medium">No se encontraron usuarios</p>
                                <p className="text-xs text-gray-400 mt-1">Intenta con otro término de búsqueda</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Lista de visualizadores seleccionados */}
            {visualizadores.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Visualizadores Agregados</h4>
                    <div className="space-y-3">
                        {visualizadores.map((visualizador, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-4 bg-gradient-to-r from-white to-blue-50 border-2 border-gray-200 rounded-xl hover:border-blue-300 transition-all shadow-sm overflow-hidden"
                            >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                                        <Eye className="h-5 w-5 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-900 truncate">
                                            {visualizador.nombre_completo}
                                        </p>
                                        <p className="text-sm text-gray-600 truncate">{visualizador.email}</p>
                                        <div className="mt-1">
                                            {visualizador.user_id ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">
                                                    <Check className="h-3 w-3 mr-1" />
                                                    Registrado
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 font-medium">
                                                    <Mail className="h-3 w-3 mr-1" />
                                                    Se registrará
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                    <button
                                        onClick={() => handleRemoveVisualizador(index)}
                                        disabled={disabled}
                                        className={`p-2 rounded-lg transition-colors ${
                                            disabled 
                                                ? 'text-gray-300 cursor-not-allowed' 
                                                : 'text-red-500 hover:bg-red-50 hover:text-red-700'
                                        }`}
                                        title="Eliminar visualizador"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Botón Agregar */}
            <div className="pt-4 border-t border-gray-200">
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    disabled={disabled}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                        disabled 
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white hover:from-blue-700 hover:to-indigo-800 shadow-sm hover:shadow-md'
                    }`}
                >
                    <Eye className="h-5 w-5" />
                    <span className="font-medium">Agregar Visualizador</span>
                </button>
            </div>

            {/* Modal para agregar nuevo visualizador */}
            {showAddForm && !disabled && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setShowAddForm(false)}
                >
                    <div 
                        className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            {/* Header del modal */}
                            <div className="flex items-center justify-between mb-6">
                                <h4 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                    <Eye className="h-6 w-6 text-blue-600" />
                                    Agregar Visualizador
                                </h4>
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            
                            {/* Formulario */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={newVisualizador.email}
                                        onChange={(e) => setNewVisualizador({...newVisualizador, email: e.target.value})}
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                                        placeholder="correo@ejemplo.com"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nombre Completo *
                                    </label>
                                    <input
                                        type="text"
                                        value={newVisualizador.nombre_completo}
                                        onChange={(e) => setNewVisualizador({...newVisualizador, nombre_completo: e.target.value})}
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                                        placeholder="Juan Pérez"
                                    />
                                </div>
                                
                                {/* Botones del modal */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={() => {
                                            setShowAddForm(false)
                                            setNewVisualizador({ email: '', nombre_completo: '' })
                                        }}
                                        className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleAddNewVisualizador}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm hover:shadow-md"
                                    >
                                        <Check className="h-5 w-5" />
                                        Agregar Visualizador
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VisualizadoresSelector;
